import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { ContextMenuOptionsForCollection } from '@frameio/collections';
import { useDuplicateCollection } from '@frameio/collections/operations';
import type {
  CollectionInfoFragment,
  CollectionNavItemFragment,
} from '@frameio/graphql-client/generated';
import { useCollectionNavItemFragment } from '@frameio/graphql-client/generated';
import type { ProjectTreeItem } from '@frameio/project-tree';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing';

type Props = {
  onDeleteClick: (collection: CollectionNavItemFragment) => void;
  item: ProjectTreeItem;
  onRenameSelect: () => void;
};

export function CollectionContextMenu({
  item,
  onDeleteClick,
  onRenameSelect,
}: Props) {
  const projectId = useActiveProjectId();
  const router = useRouter();

  const { data: collection, complete } = useCollectionNavItemFragment(item.id);

  const isPrivate = Boolean(collection.private);

  const onDuplicateCollectionSuccess = useCallback(
    (result: CollectionInfoFragment) => {
      const href = getLink({
        page: 'collection',
        collectionId: result?.id,
        projectId,
      });
      router.push(href);
    },
    [projectId, router]
  );

  const { duplicateCollection } = useDuplicateCollection({
    projectId,
    onSuccess: onDuplicateCollectionSuccess,
  });

  if (!complete) return null;

  const handleDeleteClick = () => {
    onDeleteClick(collection);
  };

  const handleDuplicateClick = () => {
    duplicateCollection(collection.id, collection.name, isPrivate);
  };

  return (
    <ContextMenuOptionsForCollection
      collectionCreatorId={collection.creator?.id ?? ''}
      collectionId={item.id}
      isCollectionPrivate={isPrivate}
      onDeleteClick={handleDeleteClick}
      onDuplicateClick={handleDuplicateClick}
      onRenameClick={onRenameSelect}
    />
  );
}
